import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Shield, Users, Plus, LogOut, Send, Crown, Trophy, Loader2, Flame, Swords, Settings, Upload, UserX, ShieldCheck, Lock, UserCheck, Check, X } from "lucide-react";
import Btn from "../components/Btn";
import api, { API, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const WS_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");

export default function Guilds() {
  const { user, refresh } = useAuth();
  const [mine, setMine] = useState(undefined); // undefined=loading
  const [members, setMembers] = useState([]);
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const [newVisibility, setNewVisibility] = useState("public");
  const [busy, setBusy] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [gform, setGform] = useState({ name: "", bio: "", visibility: "public" });
  const [reqs, setReqs] = useState([]);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [m, l] = await Promise.all([api.get("/guilds/mine"), api.get("/guilds")]);
      setMine(m.data.guild); setMembers(m.data.members || []); setList(l.data.guilds);
      if (m.data.guild) {
        setGform({ name: m.data.guild.name, bio: m.data.guild.bio || "", visibility: m.data.guild.visibility || "public" });
        if (m.data.guild.can_manage) loadReqs();
      }
    } catch { setMine(null); }
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    try { await api.patch("/guilds/settings", { name: gform.name, bio: gform.bio, visibility: gform.visibility }); toast.success("Pengaturan disimpan"); setShowSettings(false); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post("/guilds/photo", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Foto guild diperbarui"); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const setRole = async (uid, role) => {
    try { await api.post("/guilds/roles", { user_id: uid, role }); toast.success("Peran diperbarui"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const kick = async (uid) => {
    if (!window.confirm("Keluarkan anggota ini dari guild?")) return;
    try { await api.post("/guilds/kick", { user_id: uid }); toast.success("Anggota dikeluarkan"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const create = async () => {
    if (name.trim().length < 3) { toast.error("Nama guild minimal 3 karakter"); return; }
    setBusy(true);
    try { await api.post("/guilds", { name: name.trim(), visibility: newVisibility }); toast.success("Guild dibuat! 🎉"); await refresh(); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const join = async (g) => {
    try {
      const { data } = await api.post(`/guilds/${g.guild_id}/join`);
      if (data.status === "requested") toast.success("Permintaan gabung terkirim. Menunggu persetujuan ketua guild ⏳");
      else { toast.success("Bergabung ke guild!"); await refresh(); }
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const loadReqs = async () => {
    try { const { data } = await api.get("/guilds/requests"); setReqs(data.requests); }
    catch { setReqs([]); }
  };
  const reviewReq = async (request_id, approve) => {
    try { await api.post("/guilds/requests/review", { request_id, approve }); toast.success(approve ? "Anggota diterima!" : "Permintaan ditolak"); loadReqs(); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const leave = async () => {
    try { await api.post("/guilds/leave"); toast.success("Keluar dari guild."); await refresh(); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (mine === undefined) return <div className="grid place-items-center py-16"><Loader2 className="w-10 h-10 animate-spin text-su-primary" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading font-black text-2xl sm:text-3xl flex items-center gap-2"><Shield className="w-7 h-7 text-su-primary" /> Study Guild</h1>
        <p className="text-su-muted font-medium">Belajar bareng, bersaing di papan guild mingguan.</p>
      </div>

      {mine ? (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* guild header */}
            <div className="card-chunky p-6" data-testid="my-guild">
              <div className="flex items-center gap-4">
                {mine.has_photo ? (
                  <img src={`${API}/guilds/${mine.guild_id}/photo?v=${Date.now()}`} alt="" className="w-16 h-16 rounded-2xl object-cover border border-su-border" />
                ) : (
                  <div className="w-16 h-16 rounded-2xl grid place-items-center text-black font-heading font-bold text-2xl" style={{ background: mine.emblem_color }}>{mine.name[0]}</div>
                )}
                <div className="flex-1 min-w-0">
                  <h2 className="font-heading font-bold text-xl flex items-center gap-2">{mine.name}
                    <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-su-primary/15 text-su-primary">{mine.my_role === "owner" ? "Ketua" : mine.my_role}</span>
                  </h2>
                  {mine.bio && <p className="text-su-muted text-sm truncate">{mine.bio}</p>}
                  <p className="text-su-muted font-bold text-sm flex items-center gap-3 mt-0.5">
                    <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {mine.member_count}</span>
                    <span className="flex items-center gap-1 text-su-energy"><Flame className="w-4 h-4 fill-current" /> {mine.weekly_exp.toLocaleString()} EXP/mgg</span>
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  {mine.can_manage && <Btn variant="ghost" size="sm" data-testid="guild-settings-btn" onClick={() => setShowSettings((s) => !s)}><Settings className="w-4 h-4" /></Btn>}
                  <Btn variant="ghost" size="sm" data-testid="guild-leave" onClick={leave}><LogOut className="w-4 h-4" /></Btn>
                </div>
              </div>

              {showSettings && mine.can_manage && (
                <div className="mt-5 pt-5 border-t border-su-border space-y-3" data-testid="guild-settings-panel">
                  <input data-testid="settings-name" value={gform.name} onChange={(e) => setGform({ ...gform, name: e.target.value })} placeholder="Nama guild"
                    className="w-full px-3 py-2.5 rounded-xl border border-su-border bg-su-bg outline-none focus:border-su-primary" />
                  <textarea data-testid="settings-bio" value={gform.bio} onChange={(e) => setGform({ ...gform, bio: e.target.value })} rows={2} placeholder="Bio guild" maxLength={280}
                    className="w-full px-3 py-2.5 rounded-xl border border-su-border bg-su-bg outline-none focus:border-su-primary resize-none" />
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="settings-photo-input" onChange={uploadPhoto} />
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-heading font-bold">Visibilitas:</span>
                    <select data-testid="settings-visibility" value={gform.visibility} onChange={(e) => setGform({ ...gform, visibility: e.target.value })}
                      className="text-sm rounded-lg border border-su-border bg-su-bg px-2 py-1.5 outline-none">
                      <option value="public">Publik (langsung gabung)</option>
                      <option value="private">Privat (perlu persetujuan)</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <Btn variant="ghost" size="sm" data-testid="settings-photo-btn" onClick={() => fileRef.current?.click()}><Upload className="w-4 h-4" /> Foto</Btn>
                    <Btn size="sm" data-testid="settings-save" onClick={saveSettings} className="flex-1">Simpan</Btn>
                  </div>
                </div>
              )}

              {mine.can_manage && reqs.length > 0 && (
                <div className="mt-5 pt-5 border-t border-su-border" data-testid="guild-requests-panel">
                  <p className="font-heading font-black text-sm mb-3 flex items-center gap-2"><UserCheck className="w-4 h-4 text-su-primary" /> Permintaan Gabung ({reqs.length})</p>
                  <div className="space-y-2">
                    {reqs.map((r) => (
                      <div key={r.id} className="flex items-center gap-2 text-sm rounded-xl border border-su-border p-2" data-testid={`req-${r.id}`}>
                        <span className="flex-1 font-heading font-bold truncate">{r.user_name}</span>
                        <button data-testid={`req-approve-${r.id}`} onClick={() => reviewReq(r.id, true)} className="text-su-success hover:scale-110 transition-transform"><Check className="w-5 h-5" /></button>
                        <button data-testid={`req-reject-${r.id}`} onClick={() => reviewReq(r.id, false)} className="text-su-danger hover:scale-110 transition-transform"><X className="w-5 h-5" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <GuildChat guildId={mine.guild_id} me={user} />
            <GuildWars mine={mine} list={list} onChange={load} />
          </div>

          {/* members + leaderboard */}
          <div className="space-y-6">
            <div className="card-chunky p-6">
              <h3 className="font-heading font-black mb-3">Anggota</h3>
              <div className="space-y-2">
                {members.map((m, i) => {
                  const isOwnerRow = m.role === "owner";
                  const canKick = mine.can_manage && !isOwnerRow && (mine.my_role === "owner" || m.role === "member");
                  return (
                    <div key={m.user_id} className="flex items-center gap-2 text-sm" data-testid={`member-${m.user_id}`}>
                      <span className="flex-1 font-heading font-bold truncate flex items-center gap-1">
                        {m.name}{m.vip && <Crown className="w-3 h-3 text-su-secondary fill-current" />}
                        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${isOwnerRow ? "bg-su-energy/15 text-su-energy" : m.role !== "member" ? "bg-su-primary/15 text-su-primary" : "bg-su-track text-su-muted"}`}>
                          {isOwnerRow ? "Ketua" : m.role}
                        </span>
                      </span>
                      {mine.my_role === "owner" && !isOwnerRow && (
                        <select data-testid={`role-${m.user_id}`} value={m.role === "member" ? "member" : m.role} onChange={(e) => setRole(m.user_id, e.target.value)}
                          className="text-xs rounded-lg border border-su-border bg-su-bg px-1.5 py-1 outline-none">
                          <option value="member">Anggota</option>
                          {["R1", "R2", "R3", "R4"].map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      )}
                      {canKick && <button data-testid={`kick-${m.user_id}`} onClick={() => kick(m.user_id)} className="text-su-danger hover:scale-110 transition-transform"><UserX className="w-4 h-4" /></button>}
                    </div>
                  );
                })}
              </div>
            </div>
            <GuildBoard list={list} myId={mine.guild_id} />
          </div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="card-chunky p-6">
            <h2 className="font-heading font-black text-lg mb-1 flex items-center gap-2"><Plus className="w-5 h-5 text-su-primary" /> Buat Guild Baru</h2>
            <p className="text-su-muted font-medium text-sm mb-4">Jadilah ketua dan ajak temanmu bertanding.</p>
            <input data-testid="guild-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nama guild kamu"
              className="w-full px-4 py-3 rounded-2xl border-2 border-su-border bg-su-bg font-medium outline-none focus:border-su-primary mb-3" />
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm font-heading font-bold">Visibilitas:</span>
              <select data-testid="create-visibility" value={newVisibility} onChange={(e) => setNewVisibility(e.target.value)}
                className="text-sm rounded-lg border border-su-border bg-su-bg px-2 py-1.5 outline-none flex-1">
                <option value="public">Publik — siapa saja bisa langsung gabung</option>
                <option value="private">Privat — perlu persetujuan ketua</option>
              </select>
            </div>
            <Btn data-testid="guild-create" onClick={create} disabled={busy} className="w-full">{busy ? "Membuat..." : "Buat Guild"}</Btn>
          </div>
          <div className="card-chunky p-6">
            <h2 className="font-heading font-black text-lg mb-3">Gabung Guild</h2>
            {list.length === 0 ? <p className="text-su-muted font-medium text-sm">Belum ada guild. Buat yang pertama!</p> : (
              <div className="space-y-2">
                {list.map((g) => (
                  <div key={g.guild_id} className="flex items-center gap-3 rounded-2xl border-2 border-su-border p-3" data-testid={`guild-${g.guild_id}`}>
                    <div className="w-10 h-10 rounded-xl grid place-items-center text-white font-heading font-black" style={{ background: g.emblem_color }}>{g.name[0]}</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-heading font-extrabold truncate flex items-center gap-1.5">{g.name}
                        {g.visibility === "private" && <Lock className="w-3.5 h-3.5 text-su-muted shrink-0" title="Privat" />}
                      </p>
                      <p className="text-xs text-su-muted font-bold">{g.members} anggota · {g.weekly_exp} EXP/mgg</p>
                    </div>
                    <Btn size="sm" data-testid={`join-${g.guild_id}`} onClick={() => join(g)}>{g.visibility === "private" ? "Minta Gabung" : "Gabung"}</Btn>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function GuildBoard({ list, myId }) {
  return (
    <div className="card-chunky p-6">
      <h3 className="font-heading font-black mb-3 flex items-center gap-2"><Trophy className="w-5 h-5 text-su-secondary" /> Papan Guild Mingguan</h3>
      <div className="space-y-2">
        {list.map((g, i) => (
          <div key={g.guild_id} className={`flex items-center gap-2 rounded-xl p-2 border-2 ${g.guild_id === myId ? "border-su-primary bg-su-primary/5" : "border-transparent"}`}>
            <span className={`w-6 h-6 grid place-items-center rounded-lg text-xs font-heading font-black ${i === 0 ? "bg-su-secondary text-black" : "bg-su-track text-su-muted"}`}>{i + 1}</span>
            <span className="flex-1 font-heading font-extrabold text-sm truncate">{g.name}</span>
            <span className="font-mono text-su-energy text-sm">{g.weekly_exp}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function GuildChat({ guildId, me }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [online, setOnline] = useState(false);
  const wsRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    let closed = false;
    (async () => {
      let token;
      try { token = (await api.get("/auth/ws-token")).data.token; } catch { return; }
      if (closed) return;
      const ws = new WebSocket(`${WS_BASE}/api/ws/guild?token=${token}`);
      wsRef.current = ws;
      ws.onopen = () => setOnline(true);
      ws.onclose = () => setOnline(false);
      ws.onmessage = (ev) => {
        const m = JSON.parse(ev.data);
        if (m.type === "history") setMsgs(m.messages);
        else if (m.type === "message") setMsgs((prev) => [...prev, m.message]);
      };
    })();
    return () => { closed = true; wsRef.current?.close(); };
  }, [guildId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = () => {
    const t = text.trim();
    if (!t || wsRef.current?.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({ text: t }));
    setText("");
  };

  return (
    <div className="card-chunky p-0 overflow-hidden flex flex-col h-96" data-testid="guild-chat">
      <div className="px-5 py-3 border-b-2 border-su-border font-heading font-black flex items-center gap-2">
        💬 Chat Guild <span className={`w-2 h-2 rounded-full ${online ? "bg-su-success" : "bg-su-muted"}`} /> <span className="text-xs font-bold text-su-muted">{online ? "live" : "menghubungkan..."}</span>
      </div>
      <div className="flex-1 overflow-auto p-4 space-y-2">
        {msgs.length === 0 && <p className="text-su-muted font-medium text-sm text-center py-6">Belum ada pesan. Sapa anggotamu!</p>}
        {msgs.map((m, i) => {
          const mine = m.user_id === me.user_id;
          return (
            <div key={i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${mine ? "bg-su-primary text-white" : "bg-su-bg border-2 border-su-border"}`}>
                {!mine && <div className="text-[10px] font-heading font-black text-su-primary">{m.name}</div>}
                <div className="text-sm font-medium">{m.text}</div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="flex items-center gap-2 p-3 border-t-2 border-su-border">
        <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ketik pesan..." className="flex-1 px-3 py-2 rounded-xl border-2 border-su-border bg-su-bg text-sm font-medium outline-none focus:border-su-primary" />
        <button data-testid="chat-send" onClick={send} className="w-10 h-10 rounded-xl bg-su-primary text-white grid place-items-center"><Send className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

function GuildWars({ mine, list, onChange }) {
  const nav = useNavigate();
  const [wars, setWars] = useState([]);
  const [oppId, setOppId] = useState("");
  const [warExam, setWarExam] = useState("CPNS");
  const others = list.filter((g) => g.guild_id !== mine.guild_id);

  const load = () => api.get("/guild-wars").then(({ data }) => setWars(data.wars)).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); onChange && onChange(); } catch (e) { toast.error(apiErr(e)); } };
  const challenge = () => { if (!oppId) return; act(() => api.post("/guild-wars/challenge", { opponent_guild_id: oppId, exam: warExam }), "Tantangan dikirim! ⚔️"); };
  const accept = (id) => act(() => api.post(`/guild-wars/${id}/accept`), "Tantangan diterima!");
  const finalize = (id) => act(() => api.post(`/guild-wars/${id}/finalize`), "Perang diselesaikan!");
  const play = async (id) => {
    try { const { data } = await api.post(`/guild-wars/${id}/play`); nav(`/cbt/${data.attempt_id}`, { state: data }); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="card-chunky p-6" data-testid="guild-wars">
      <h3 className="font-heading font-black text-lg mb-3 flex items-center gap-2"><Swords className="w-5 h-5 text-su-danger" /> Guild War</h3>

      {mine.is_owner && (
        <div className="flex flex-wrap gap-2 mb-4">
          <select data-testid="war-opponent" value={oppId} onChange={(e) => setOppId(e.target.value)}
            className="flex-1 min-w-[160px] px-3 py-2.5 rounded-xl border-2 border-su-border bg-su-bg font-medium outline-none focus:border-su-primary">
            <option value="">Pilih guild lawan...</option>
            {others.map((g) => <option key={g.guild_id} value={g.guild_id}>{g.name}</option>)}
          </select>
          <select data-testid="war-exam" value={warExam} onChange={(e) => setWarExam(e.target.value)}
            className="px-3 py-2.5 rounded-xl border-2 border-su-border bg-su-bg font-medium outline-none focus:border-su-primary">
            <option value="CPNS">Soal CPNS</option>
            <option value="SNBT">Soal SNBT</option>
          </select>
          <Btn variant="danger" data-testid="war-challenge" disabled={!oppId} onClick={challenge}>Tantang</Btn>
        </div>
      )}

      {wars.length === 0 ? (
        <p className="text-su-muted font-medium text-sm">Belum ada perang. {mine.is_owner ? "Tantang guild lain di atas!" : "Tunggu ketua guild menantang."}</p>
      ) : (
        <div className="space-y-3">
          {wars.map((w) => {
            const myScore = w.scores[mine.guild_id] || 0;
            const oppGid = w.guild_a === mine.guild_id ? w.guild_b : w.guild_a;
            const oppScore = w.scores[oppGid] || 0;
            const oppName = w.guild_a === mine.guild_id ? w.name_b : w.name_a;
            const won = w.winner === mine.guild_id;
            return (
              <div key={w.war_id} className="rounded-2xl border-2 border-su-border p-4" data-testid={`war-${w.war_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-heading font-extrabold text-sm">vs {oppName}</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-heading font-black px-2 py-0.5 rounded-full bg-su-primary/10 text-su-primary">{w.exam || "CPNS"}</span>
                    <span className={`text-xs font-heading font-black px-2 py-0.5 rounded-full ${
                      w.status === "active" ? "bg-su-success/15 text-su-success" : w.status === "pending" ? "bg-su-energy/15 text-su-energy" : "bg-su-track text-su-muted"
                    }`}>{w.status === "active" ? "BERLANGSUNG" : w.status === "pending" ? "MENUNGGU" : "SELESAI"}</span>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-4 font-mono font-bold text-xl mb-3">
                  <span className="text-su-primary">{myScore}</span><span className="text-su-muted text-sm">vs</span><span className="text-su-danger">{oppScore}</span>
                </div>
                {w.status === "finished" && <p className={`text-center font-heading font-black text-sm mb-2 ${won ? "text-su-success" : w.winner ? "text-su-danger" : "text-su-muted"}`}>{won ? "🏆 GUILD-MU MENANG!" : w.winner ? "Kalah" : "Seri"}</p>}
                <div className="flex gap-2">
                  {w.status === "pending" && w.my_side === "b" && mine.is_owner && <Btn size="sm" variant="success" data-testid={`war-accept-${w.war_id}`} onClick={() => accept(w.war_id)} className="flex-1">Terima</Btn>}
                  {w.status === "active" && <Btn size="sm" data-testid={`war-play-${w.war_id}`} onClick={() => play(w.war_id)} className="flex-1">Bertanding</Btn>}
                  {w.status === "active" && mine.is_owner && <Btn size="sm" variant="ghost" data-testid={`war-finalize-${w.war_id}`} onClick={() => finalize(w.war_id)}>Selesaikan</Btn>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
